import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";

export interface WorkingHoursConfig {
  type: "default" | "recurring";
  date?: string;
  recurringenabled?: boolean;
  recurrenceType?: "day" | "weekly" | "biweekly" | "monthly";
  interval?: number;
  selectedDays?: ("monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" | "sunday")[];
  monthlyPattern?: string;
  startTime: string;
  endTime: string;
  totalHours: number;
  child?: Array<{
    date: string;
    startTime: string;
    endTime: string;
    totalHours: number;
  }>;
}

export interface OvertimeConfig {
  allowed: boolean;
  maxHoursPerDay?: number;
  maxHoursPerMonth?: number;
  overtimeRate?: number;
  requiresApproval: boolean;
}

export interface LateArrivalConfig {
  graceMinutes: number;
  markAsHalfDayAfter?: number;
  deductionEnabled?: boolean;
  deductMinutesFor?: number;
  forEveryMinutes?: number;
}

export interface EarlyDepartureConfig {
  graceMinutes: number;
  markHalfDayBeforeMinutes?: number;
}

export interface DepartmentBreak {
  type: "duration" | "timeRange";
  durationHours?: number;
  hours?: number;
  startTime?: string;
  endTime?: string;
}

export interface DepartmentLocation {
  lat: number | string;
  lng: number | string;
}

export interface Department {
  _id: string;
  name: string;
  description?: string;
  handbook?: string;
  status: "active" | "inactive";
  organization: string;
  members: string[];
  workingHours?: WorkingHoursConfig[];
  overtimeConfig?: OvertimeConfig;
  lateArrivalConfig?: LateArrivalConfig;
  earlyDepartureConfig?: EarlyDepartureConfig;
  break?: DepartmentBreak;
  location?: DepartmentLocation;
  isLocationCheck?: boolean;
  director?: string;
  logo?: string;
  timezone?: string;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
  __v?: number;
}

export type DepartmentsPayload = {
  name: string;
  description?: string;
  handbook?: string;
  status: "active" | "inactive";
  organization: string;
  members?: string[];
  workingHours?: WorkingHoursConfig[] | null;
  overtimeConfig?: OvertimeConfig | null;
  lateArrivalConfig?: LateArrivalConfig | null;
  earlyDepartureConfig?: EarlyDepartureConfig | null;
  break?: DepartmentBreak | null;
  location?: DepartmentLocation | null;
  isLocationCheck?: boolean;
  director?: string;
  departmentLogo?: string | null;
  timezone?: string;
};

export type DepartmentsResponse = {
  success: boolean;
  message: string;
  data: Department;
};

const updateDepartment = async (
  id: string,
  payload: DepartmentsPayload
): Promise<DepartmentsResponse> => {
  const { data } = await customAxios.put<DepartmentsResponse>(
    `/departments/${id}`,
    payload
  );
  return data;
};

export const useUpdateDepartment = () => {
  const queryClient = useQueryClient();

  return useMutation<
    DepartmentsResponse,
    AxiosError,
    { id: string; payload: DepartmentsPayload }
  >(({ id, payload }) => updateDepartment(id, payload), {
    onSuccess: async (data, variables) => {
      showNotification({
        title: "Success",
        message: data.message ?? "Department updated",
        color: "green",
      });

      await queryClient.invalidateQueries(["departments"]);
      await queryClient.invalidateQueries(["department", variables.id]);
    },
    onError: (error) =>
      showNotification({
        title: "Error",
        message:
          (error.response?.data as { message?: string })?.message ??
          "Failed to update department",
        color: "red",
      }),
  });
};
